import React, { useRef } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SkeletonBox } from '@eduact/ed-components'
import { Icon as AbwIcon } from '@Components/AbwaabPlus'
import { useGetStandaloneCoursesQuery } from '@Features/StandaloneCourse/StandaloneCourseApi/StandaloneCourseApi'
import StandaloneCourseCard from './StandaloneCourseCard'
import usePlatformFlags from '@Hooks/usePlatformFlags'

/**
 * "الكورسات المتاحة" — the courses twin of the guest الفصول المتاحة carousel:
 * the catalog's first page as a swipeable rail of the shared course cards,
 * عرض الكل → the الكورسات search tab. Self-hides when the platform is off or
 * the catalog is empty.
 */
const GuestLandingCourses: React.FC = () => {
	const [t, i18n] = useTranslation('standalone_course')
	const { t: tLanding } = useTranslation('landingPage')
	const { coursesEnabled } = usePlatformFlags()
	const navigate = useNavigate()
	const railRef = useRef<HTMLDivElement>(null)
	const isRtl = i18n.dir() === 'rtl'

	const { data, isLoading, isFetching } = useGetStandaloneCoursesQuery({ page: 1, perPage: 12 }, { skip: !coursesEnabled })
	const loading = isLoading || isFetching
	const courses = data?.data ?? []

	if (!coursesEnabled) return null
	if (!loading && courses.length === 0) return null

	const scrollRail = (dir: 'prev' | 'next') => {
		const el = railRef.current
		if (!el) return
		const amount = el.clientWidth * 0.8
		const sign = dir === 'prev' ? (isRtl ? 1 : -1) : isRtl ? -1 : 1
		el.scrollBy({ left: sign * amount, behavior: 'smooth' })
	}

	return (
		<Section>
			<HeadRow>
				<Title>{t('available_courses', { defaultValue: 'الكورسات المتاحة' })}</Title>
				<HeadControls>
					<ArrowGroup>
						<RoundArrow type="button" aria-label="prev" onClick={() => scrollRail('prev')}>
							<span style={{ transform: isRtl ? 'scaleX(-1)' : 'none', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
						<RoundArrow type="button" aria-label="next" onClick={() => scrollRail('next')}>
							<span style={{ transform: isRtl ? 'none' : 'scaleX(-1)', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
					</ArrowGroup>
					<SeeAll type="button" onClick={() => navigate('/search?tab=courses')}>
						{tLanding('courses.see_all', { defaultValue: 'عرض الكل' })}
						<Chevron>
							<AbwIcon name="chevron-left" size={14} />
						</Chevron>
					</SeeAll>
				</HeadControls>
			</HeadRow>
			<Rail ref={railRef}>
				{loading && Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} isLoading width="100%" height="200px" />)}
				{!loading && courses.map(course => <StandaloneCourseCard key={`guest-course-${course.id}`} course={course} />)}
			</Rail>
		</Section>
	)
}

export default GuestLandingCourses

/* Identical shell to GuestLandingClassrooms — the rails must read as twins. */
const Section = styled.section`
	padding: 12px 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`
const HeadRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`
const Title = styled.h2`
	margin: 0;
	font: var(--fw-bold) var(--fs-body-lg) var(--font-sans);
	color: var(--text-primary);
	@media (min-width: 1024px) {
		font-size: 22px;
	}
`
const HeadControls = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;
`
const ArrowGroup = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: flex;
		gap: 4px;
	}
`
const RoundArrow = styled.button`
	width: 28px;
	height: 28px;
	border: 1px solid var(--border);
	border-radius: var(--r-full);
	background: var(--surface-card);
	color: var(--text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
`
const SeeAll = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 2px;
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	color: var(--blue);
`
const Chevron = styled.span`
	display: flex;
	html:not([dir='rtl']) & {
		transform: scaleX(-1);
	}
`
const Rail = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 6px;
	scroll-snap-type: x proximity;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	> * {
		flex: 0 0 calc((100vw - 40px) / 2);
		max-width: 236px;
		scroll-snap-align: start;
	}
	@media (min-width: 1024px) {
		gap: 8px;
		> * {
			flex: 0 0 208px;
			max-width: none;
		}
	}
`
